import {
  createContext,
  type ReactElement,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export type ModalContextType = {
  openModal: (id: string) => void;
  closeModal: () => void;
  isOpen: (id: string) => boolean;
  isAnyModalOpen: boolean;
};

type ModalProviderProps = {
  readonly children: ReactElement;
};

const ModalContext = createContext<ModalContextType>({} as ModalContextType);

export const useModal = (): ModalContextType => useContext(ModalContext);

export function ModalProvider({ children }: ModalProviderProps): ReactElement {
  const [activeModal, setActiveModal] = useState<string | null>(null);

  const openModal = useCallback((id: string) => {
    setActiveModal(id);
  }, []);

  const closeModal = useCallback(() => {
    setActiveModal(null);
  }, []);

  const isOpen = useCallback((id: string) => activeModal === id, [activeModal]);

  const isAnyModalOpen = useMemo(() => !!activeModal, [activeModal]);

  const modalContextValue = useMemo<ModalContextType>(
    () => ({
      openModal,
      closeModal,
      isOpen,
      isAnyModalOpen,
    }),
    [openModal, closeModal, isOpen, isAnyModalOpen],
  );

  return <ModalContext.Provider value={modalContextValue}>{children}</ModalContext.Provider>;
}
